/**
 * @fileoverview Application settings dialog management.
 * @module ui/SettingsViewer
 */

import { UiManager } from './UiManager.js';
import { StatefulViewer } from './StatefulViewer.js';
import { MAPAPP_DEFAULTS, THEMES, DOM_ELEMENTS } from '../constants.js';

const { ID_SAVE_SETTINGS, ID_ENABLE_ADVANCED_SEARCH, ID_THEME_SELECT,
    ID_INACTIVE_LAYER_OPACITY, ID_OUTSIDE_REGION_OPACITY, ID_SHOW_EXTRA_PCAT_INFO,
    ID_YELLOW_MARKER_STYLE, ID_HIDE_LOG_PANEL, ID_SHOW_EXTRA_ACTION_BUTTONS,
    ID_STAY_ON_MAP_AFTER_READ, ID_PDF_AUTO_HIGHLIGHT_ON_COPY,
    ID_PDF_POPUP_MEDIA_FILES,
    ID_RIGHT_MARGIN_GUTTER, ID_FIT_SIZE_ZOOM,
    ID_PLAY_LOG_FONT_SIZE } = DOM_ELEMENTS;


/**
 * Manages the application settings modal dialog.
 * Handles settings persistence via StateManager and UI controls
 * for advanced search toggle and data clearing.
 * @class
 * @extends StatefulViewer
 */
export class SettingsViewer extends StatefulViewer {
    /** @constant {string} Modal element ID */
    static MODAL_ID = 'settingsModal';

    /**
     * Creates a new SettingsViewer instance.
     * @param {Object} mapApp - The MapApp instance
     * @param {string} containerId - ID of the container element for settings content
     */
    constructor(mapApp, containerId) {
        super(mapApp, containerId);

        this.renderSettings();
        this.setupEventListeners();
        this.restoreSettings();
        this.colorizeMarkerSvgs();
    }


    /**
     * Shows the settings modal dialog.
     * Restores current settings before displaying.
     */
    showModal() {
        // (before restoreSettings, which marks the saved marker style as selected in the picker)
        this.refreshMarkerStylePickerIfEmpty();
        this.restoreSettings();
        this.updateStorageSpaceDisplay();
        this.updateSessionLogDisplay();
        this.showModalById(SettingsViewer.MODAL_ID);
    }

    /**
     * Renders the settings form HTML structure.
     * Includes advanced search toggle and clear data button.
     * @private
     */
    renderSettings() {
        this.container.innerHTML = `
            <div class="settings-viewer">
                <ul class="nav nav-tabs mb-0" id="settingsTabs" role="tablist">
                    <li class="nav-item" role="presentation">
                        <button class="nav-link active" id="settings-ui-tab" data-bs-toggle="tab" data-bs-target="#settings-ui" type="button" role="tab" aria-controls="settings-ui" aria-selected="true">User Interface</button>
                    </li>
                    <li class="nav-item" role="presentation">
                        <button class="nav-link" id="settings-features-tab" data-bs-toggle="tab" data-bs-target="#settings-features" type="button" role="tab" aria-controls="settings-features" aria-selected="false">Authors</button>
                    </li>
                    <li class="nav-item" role="presentation">
                        <button class="nav-link" id="settings-markers-tab" data-bs-toggle="tab" data-bs-target="#settings-markers" type="button" role="tab" aria-controls="settings-markers" aria-selected="false">Markers</button>
                    </li>
                    <li class="nav-item" role="presentation">
                        <button class="nav-link" id="settings-pdf-tab" data-bs-toggle="tab" data-bs-target="#settings-pdf" type="button" role="tab" aria-controls="settings-pdf" aria-selected="false">Pdf Reader</button>
                    </li>
                    <li class="nav-item" role="presentation">
                        <button class="nav-link" id="settings-tools-tab" data-bs-toggle="tab" data-bs-target="#settings-tools" type="button" role="tab" aria-controls="settings-tools" aria-selected="false">Tools</button>
                    </li>
                    <li class="nav-item" role="presentation">
                        <button class="nav-link" id="settings-log-tab" data-bs-toggle="tab" data-bs-target="#settings-log" type="button" role="tab" aria-controls="settings-log" aria-selected="false">Log</button>
                    </li>
                </ul>

                <div class="tab-content" id="settingsTabsContent">
                    <div class="tab-pane fade show active" id="settings-ui" role="tabpanel" aria-labelledby="settings-ui-tab">
                        <div class="mb-3">
                            <label for="themeSelect" class="form-label">Theme:</label>
                            <select class="form-select form-select-sm" id="${ID_THEME_SELECT}">
                                ${THEMES.map(theme => `<option value="${theme.id}">${theme.label}</option>`).join('')}
                            </select>
                        </div>

                        <div class="mb-3 settings-opacity-row" title="Controls opacity of inactive map layers (default: ${Math.round(MAPAPP_DEFAULTS.INACTIVE_LAYER_OPACITY * 100)}%)">
                            <label for="inactiveLayerOpacityEl" class="form-label mb-0">
                                Inactive layer opacity: <span id="inactiveLayerOpacityElValue">100%</span>
                            </label>
                            <input type="range" class="form-range" id="${ID_INACTIVE_LAYER_OPACITY}" min="25" max="100" step="1" value="100">
                        </div>

                        <div class="mb-3 settings-opacity-row" title="Controls opacity of map areas outside selected region (default: ${Math.round(MAPAPP_DEFAULTS.OUTSIDE_REGION_OPACITY * 100)}%)">
                            <label for="outsideRegionOpacityEl" class="form-label mb-0">
                                Outside region opacity: <span id="outsideRegionOpacityElValue">100%</span>
                            </label>
                            <input type="range" class="form-range" id="${ID_OUTSIDE_REGION_OPACITY}" min="5" max="100" step="1" value="100">
                        </div>

                        <div class="form-check form-switch mb-3">
                            <input class="form-check-input" type="checkbox" id="${ID_HIDE_LOG_PANEL}">
                            <label class="form-check-label" for="${ID_HIDE_LOG_PANEL}">
                                Hide Log Panel
                            </label>
                            <small class="form-text text-muted d-block mt-1">
                                Hide the Play Log panel from the main sidebar and do not keep track of actions taken.
                            </small>
                        </div>

                        <div class="mb-3">
                            <label for="${ID_PLAY_LOG_FONT_SIZE}" class="form-label mb-0">
                                Play Log Font Size: <span id="${ID_PLAY_LOG_FONT_SIZE}Value">12</span>
                            </label>
                            <input type="range" class="form-range" id="${ID_PLAY_LOG_FONT_SIZE}" min="6" max="25" step="1" value="12">
                        </div>
                    </div>

                    <div class="tab-pane fade" id="settings-features" role="tabpanel" aria-labelledby="settings-features-tab">
                        <div class="form-check form-switch mb-3">
                            <input class="form-check-input" type="checkbox" id="${ID_ENABLE_ADVANCED_SEARCH}">
                            <label class="form-check-label" for="enableAdvancedSearch">
                                Enable advanced search tab
                            </label>
                            <small class="form-text text-muted d-block mt-1">
                                Enables an additional search tab to allow case authors to conduct wildcard and other advances searches. Should not be used by normal players as it would be considered cheating.
                            </small>
                        </div>
                        <div class="form-check form-switch mb-3">
                            <input class="form-check-input" type="checkbox" id="${ID_SHOW_EXTRA_PCAT_INFO}">
                            <label class="form-check-label" for="showExtraPcatInfo">
                                Show extra POI category info
                            </label>
                            <small class="form-text text-muted d-block mt-1">
                                Enabling this option will show information about business categories and ethnicities of residents that should not normally be visible to players. Players should not enable this.
                            </small>
                        </div>
                        <div class="form-check form-switch mb-3">
                            <input class="form-check-input" type="checkbox" id="${ID_SHOW_EXTRA_ACTION_BUTTONS}">
                            <label class="form-check-label" for="${ID_SHOW_EXTRA_ACTION_BUTTONS}">
                                Show extra result action buttons
                            </label>
                            <small class="form-text text-muted d-block mt-1">
                                Enabling this option will show an extra action button for results that will let you do things like geolocate the address on an external historical map/photo website.
                            </small>
                        </div>
                    </div>

                    <div class="tab-pane fade" id="settings-markers" role="tabpanel" aria-labelledby="settings-markers-tab">
                        <label class="form-label">Place marker style:</label>
                        <div class="marker-style-picker" id="${ID_YELLOW_MARKER_STYLE}">
                            ${this.renderMarkerStyleItems()}
                        </div>
                    </div>

                    <div class="tab-pane fade" id="settings-log" role="tabpanel" aria-labelledby="settings-log-tab">
                        <pre id="sessionLogContent" style="font-family: monospace; font-size: 0.65rem; overflow: auto; white-space: pre; background: var(--bs-body-bg); border: 1px solid var(--bs-border-color); padding: 0.5rem; margin: 0; flex: 1; min-height: 0;"></pre>
                    </div>

                    <div class="tab-pane fade" id="settings-pdf" role="tabpanel" aria-labelledby="settings-pdf-tab">
                        <div class="form-check form-switch mb-3">
                            <input class="form-check-input" type="checkbox" id="${ID_STAY_ON_MAP_AFTER_READ}">
                            <label class="form-check-label" for="${ID_STAY_ON_MAP_AFTER_READ}">
                                Stay on map
                            </label>
                            <small class="form-text text-muted d-block mt-1">
                                Stay on map even when clicking to icon to read a lead in case booklet (useful for hybrid paper&amp;digital play).
                            </small>
                        </div>

                        <div class="form-check form-switch mb-3">
                            <input class="form-check-input" type="checkbox" id="${ID_PDF_AUTO_HIGHLIGHT_ON_COPY}">
                            <label class="form-check-label" for="${ID_PDF_AUTO_HIGHLIGHT_ON_COPY}">
                                Highlight on copy
                            </label>
                            <small class="form-text text-muted d-block mt-1">
                                Automatically highlight any select when it is copied
                            </small>
                        </div>

                        <div class="form-check form-switch mb-3">
                            <input class="form-check-input" type="checkbox" id="${ID_PDF_POPUP_MEDIA_FILES}">
                            <label class="form-check-label" for="${ID_PDF_POPUP_MEDIA_FILES}">
                                Pop-up media files
                            </label>
                            <small class="form-text text-muted d-block mt-1">
                                Open clicked media urls in a pop-up dialog instead of new tab.
                            </small>
                        </div>

                        <div class="mb-3">
                            <label for="${ID_RIGHT_MARGIN_GUTTER}" class="form-label mb-0">
                                Right margin gutter: <span id="${ID_RIGHT_MARGIN_GUTTER}Value">0</span>
                            </label>
                            <input type="range" class="form-range" id="${ID_RIGHT_MARGIN_GUTTER}" min="0" max="100" step="1" value="0">
                            <small class="form-text text-muted d-block mt-1">
                                Add an extra gutter at right side of pdf reader.
                            </small>
                        </div>

                        <div class="mb-3">
                            <label for="${ID_FIT_SIZE_ZOOM}" class="form-label mb-0">
                                Extra zoom when fitting width/page: <span id="${ID_FIT_SIZE_ZOOM}Value">0</span>
                            </label>
                            <input type="range" class="form-range" id="${ID_FIT_SIZE_ZOOM}" min="0" max="50" step="1" value="0">
                            <small class="form-text text-muted d-block mt-1">
                                Additional zoom when fitting width or page, in order to consume margins.
                            </small>
                        </div>
                    </div>

                    <div class="tab-pane fade" id="settings-tools" role="tabpanel" aria-labelledby="settings-tools-tab">
                        <h6 class="text-muted mb-3">Utility Actions</h6>
                        <div class="mb-3">
                            <button type="button" class="btn btn-outline-primary btn-sm" id="exportSettingsBtn">
                                <i class="bi bi-download"></i> Export all settings
                            </button>
                            <small class="form-text text-muted d-block mt-1">
                                Export all saved settings, bookmarks, history, and preferences to a JSON file for backup or transfer to another browser.
                            </small>
                        </div>
                        <div class="mb-3">
                            <button type="button" class="btn btn-outline-primary btn-sm" id="importSettingsBtn">
                                <i class="bi bi-upload"></i> Import all settings
                            </button>
                            <small class="form-text text-muted d-block mt-1">
                                Import settings from a previously exported JSON file. This will *REPLACE* and OVERWRITE *ALL* of your current bookmarks, saved searches, and general settings.
                            </small>
                        </div>
                        <hr class="my-3">
                        <div class="mb-3">
                            <button type="button" class="btn btn-outline-danger btn-sm" id="clearStateBtn">
                                <i class="bi bi-trash"></i> Delete all site settings
                            </button>
                            <small class="form-text text-muted d-block mt-1">
                                Delete ALL saved data for this web app including all user interface preferences, bookmarks, history, and other settings. WARNING: This cannot be undone.
                                <span class="storageUsed">Current storage used: <span id="storageSpaceUsed"></span></span>.
                            </small>
                        </div>
                    </div>
                </div>
            </div>
        `;
    }

    /**
     * Sets up event listeners for save button, clear state, and modal events.
     * @private
     */
    setupEventListeners() {
        // Get the Save Changes button from the modal footer
        const saveButton = document.getElementById(ID_SAVE_SETTINGS);
        if (saveButton) {
            saveButton.addEventListener('click', () => this.saveSettings());
        }

        // Clear state button
        const clearStateBtn = this.container.querySelector('#clearStateBtn');
        if (clearStateBtn) {
            clearStateBtn.addEventListener('click', () => this.handleClearState());
        }

        // Export settings button
        const exportSettingsBtn = this.container.querySelector('#exportSettingsBtn');
        if (exportSettingsBtn) {
            exportSettingsBtn.addEventListener('click', () => this.handleExportSettings());
        }

        // Import settings button
        const importSettingsBtn = this.container.querySelector('#importSettingsBtn');
        if (importSettingsBtn) {
            importSettingsBtn.addEventListener('click', () => this.handleImportSettings());
        }

        // Inactive layer opacity slider - update display value on input
        const opacitySlider = this.container.querySelector('#' + ID_INACTIVE_LAYER_OPACITY);
        if (opacitySlider) {
            opacitySlider.addEventListener('input', () => this.updateOpacityDisplay());
        }

        // Outside region opacity slider - update display value on input
        const outsideRegionSlider = this.container.querySelector('#' + ID_OUTSIDE_REGION_OPACITY);
        if (outsideRegionSlider) {
            outsideRegionSlider.addEventListener('input', () => this.updateOutsideRegionOpacityDisplay());
        }

        // Right margin gutter slider - update display value on input
        const rightMarginGutterSlider = this.container.querySelector('#' + ID_RIGHT_MARGIN_GUTTER);
        if (rightMarginGutterSlider) {
            rightMarginGutterSlider.addEventListener('input', () => this.updateRightMarginGutterDisplay());
        }

        // Fit size zoom slider - update display value on input
        const fitSizeZoomSlider = this.container.querySelector('#' + ID_FIT_SIZE_ZOOM);
        if (fitSizeZoomSlider) {
            fitSizeZoomSlider.addEventListener('input', () => this.updateFitSizeZoomDisplay());
        }

        // Play log font size slider - update display value on input
        const playLogFontSizeSlider = this.container.querySelector('#' + ID_PLAY_LOG_FONT_SIZE);
        if (playLogFontSizeSlider) {
            playLogFontSizeSlider.addEventListener('input', () => this.updatePlayLogFontSizeDisplay());
        }

        // Marker style picker click delegation
        const markerPicker = this.container.querySelector('#' + ID_YELLOW_MARKER_STYLE);
        if (markerPicker) {
            markerPicker.addEventListener('click', (e) => {
                const item = e.target.closest('.marker-style-item');
                if (!item) return;
                markerPicker.querySelectorAll('.marker-style-item').forEach(el => el.classList.remove('selected'));
                item.classList.add('selected');
            });
        }

        // Set up blur handler
        this.setupModalBlurHandler(SettingsViewer.MODAL_ID);

        // NOTE: there is deliberately no 'hidden.bs.modal' -> applySettings() listener any more.
        // saveSettings() already applies the settings it saves, so the extra listener applied them a
        // second time on Save, and on Cancel/close applied unchanged settings, which re-ran every
        // search on every searcher (applySettings -> mapApp.onSettingsSaved).
    }

    /**
     * Handles the clear state button click with confirmation dialog.
     * Clears all app data and reloads the page.
     * @private
     */
    handleClearState() {
        // Defer confirm() so the current UI event (Bootstrap modal/dropdown)
        // can finish processing before the blocking dialog appears.
        setTimeout(() => {
            if (confirm('Are you sure you want to delete all saved data for this app from your web browser? This will clear your saved preferences, history, bookmark, etc., and cannot be undone. The page will reload after clearing.')) {
                this.getStateManager().clearStateApp();

                // Close the modal
                this.hideModalById(SettingsViewer.MODAL_ID);

                // Reload the page to apply default state
                window.location.reload();
            }
        }, 0);
    }

    /**
     * Handles the export settings button click.
     * Exports all localStorage keys with the app prefix to a JSON file.
     * @private
     */
    handleExportSettings() {
        const stateManager = this.getStateManager();
        const appPrefix = stateManager.getStoragePrefixToApp();

        // Collect all localStorage keys that start with the app prefix
        const exportData = {};
        for (const { key, value } of this.readAppStorageEntries(appPrefix)) {
            // Try to parse as JSON, otherwise store as string
            try {
                exportData[key] = JSON.parse(value);
            } catch {
                exportData[key] = value;
            }
        }

        // Create JSON content with metadata
        const exportPayload = {
            _meta: {
                exportedAt: new Date().toISOString(),
                appPrefix: appPrefix,
                version: 1
            },
            data: exportData
        };

        const jsonContent = JSON.stringify(exportPayload, null, 2);

        // Generate filename with date
        const date = new Date().toISOString().slice(0, 10);
        const filename = `casebookmap-settings-${date}.json`;

        // Download the file
        this.downloadFile(filename, jsonContent, 'application/json');
    }

    /**
     * Downloads content as a file.
     * @param {string} filename - The filename to save as
     * @param {string} content - The content to save
     * @param {string} mimeType - The MIME type for the file
     * @private
     */
    downloadFile(filename, content, mimeType) {
        const blob = new Blob([content], { type: mimeType });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }

    /**
     * Handles the import settings button click.
     * Shows an import dialog and restores settings from a JSON file.
     * @private
     */
    async handleImportSettings() {
        // Create file input for JSON selection
        const fileInput = document.createElement('input');
        fileInput.type = 'file';
        fileInput.accept = '.json';

        fileInput.addEventListener('change', async (e) => {
            const file = e.target.files[0];
            if (!file) return;

            try {
                const text = await this.readFile(file);
                const importPayload = JSON.parse(text);

                // Validate the import data structure
                if (!importPayload || !importPayload.data || typeof importPayload.data !== 'object' || Array.isArray(importPayload.data)) {
                    alert('Invalid settings file: missing data section.');
                    return;
                }

                // Validate every entry BEFORE anything is written: only this app's own keys, with
                // values of the shape the app writes (the import dialog's item checks included); the
                // rest is refused and listed in the confirmation below
                const stateManager = this.getStateManager();
                const appPrefix = stateManager.getStoragePrefixToApp();
                const { accepted, refused, dropped, droppedItemCount } = this.mapApp.getImportExportHelper().validateSettingsBackup(importPayload.data, appPrefix);
                const refusedReport = this.describeRefusedBackupEntries(refused, dropped, droppedItemCount);
                if (refused.length > 0) {
                    console.warn(`CasebookMap settings import: refused ${refused.length} entr${refused.length === 1 ? 'y' : 'ies'}:`, refused);
                }
                if (dropped.length > 0) {
                    console.warn(`CasebookMap settings import: left out ${droppedItemCount} item(s) or value(s) inside accepted entries:`, dropped);
                }
                if (accepted.length === 0) {
                    alert(`Nothing in this settings file can be imported.${refusedReport}`);
                    return;
                }

                // Confirm before importing
                const exportDate = importPayload._meta?.exportedAt
                    ? new Date(importPayload._meta.exportedAt).toLocaleString()
                    : 'unknown';

                if (!confirm(`Import ${accepted.length} settings from backup (exported ${exportDate})?${refusedReport}\n\nThis will replace your current settings and reload the page.`)) {
                    return;
                }

                // Keep a copy of what is stored now, so that a write failing part way (the browser's
                // storage full) can put it back. The import used to clear everything first, log a
                // failed write to the console only, and reload on whatever fraction had been written.
                const previousEntries = this.readAppStorageEntries(appPrefix);

                // Clear existing app settings first
                stateManager.clearStateApp();

                // Import the validated settings
                const importFailures = this.writeStorageEntries(accepted);
                if (importFailures.length > 0) {
                    const importFailure = importFailures[0];
                    console.error(`ERROR: Failed to import key ${importFailure.key}:`, importFailure.error);
                    // back to the settings as they were (they fitted before), then the same reload,
                    // since clearStateApp also reset the state this page holds in memory
                    stateManager.clearStorageByPrefix(appPrefix);
                    const restoreFailures = this.writeStorageEntries(previousEntries, false);
                    for (const restoreFailure of restoreFailures) {
                        console.error(`ERROR: Failed to restore key ${restoreFailure.key}:`, restoreFailure.error);
                    }
                    const reason = (importFailure.error && importFailure.error.message) ? importFailure.error.message : String(importFailure.error);
                    alert(`The settings could not be imported: "${String(importFailure.key).slice(0, 80)}" could not be stored (${reason}). `
                        + 'The browser\'s storage for this site may be full.\n\n'
                        + ((restoreFailures.length > 0)
                            ? `${restoreFailures.length} of your previous settings could not be put back; they are listed in the browser console.`
                            : 'Your previous settings were kept.')
                        + ' The page will reload.');
                }

                // Close the modal and reload
                this.hideModalById(SettingsViewer.MODAL_ID);
                window.location.reload();

            } catch (error) {
                console.error('ERROR: Failed to import settings:', error);
                alert(`Failed to import settings: ${error.message}`);
            }
        });

        // Trigger file selection dialog
        fileInput.click();
    }

    /**
     * Every localStorage entry of this app (its keys start with the app prefix), as stored.
     * @param {string} appPrefix - The app's storage prefix
     * @returns {Array<{key: string, value: string}>} The entries
     * @private
     */
    readAppStorageEntries(appPrefix) {
        const entries = [];
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && key.startsWith(appPrefix)) {
                entries.push({ key, value: localStorage.getItem(key) });
            }
        }
        return entries;
    }

    /**
     * Writes the entries into localStorage.
     * @param {Array<{key: string, value: string}>} entries - The entries to write
     * @param {boolean} [stopAtFirstFailure=true] - Stop at the first entry that cannot be stored
     *   (an import), or try every one (putting the previous settings back)
     * @returns {Array<{key: string, error: *}>} The entries that could not be stored, with their errors
     * @private
     */
    writeStorageEntries(entries, stopAtFirstFailure = true) {
        const failures = [];
        for (const { key, value } of entries) {
            try {
                localStorage.setItem(key, value);
            } catch (error) {
                failures.push({ key, error });
                if (stopAtFirstFailure) {
                    break;
                }
            }
        }
        return failures;
    }

    /**
     * Describes what of a settings backup will not be restored, for the confirmation text ('' if
     * nothing): the refused entries and the items / values left out of accepted ones, each by name.
     * @param {Array<{key: string, reason: string}>} refused - The refused entries
     * @param {Array<{key: string, what: string}>} dropped - What is left out of accepted entries
     * @param {number} droppedItemCount - How many items / values that is in all
     * @returns {string} Plain text, starting with a blank line
     * @private
     */
    describeRefusedBackupEntries(refused, dropped, droppedItemCount) {
        const maxLines = 10;
        const describe = (key, text) => `  ${String(key).slice(0, 80)}: ${String(text).slice(0, 200)}`;
        let text = '';
        if (refused.length > 0) {
            const shown = refused.slice(0, maxLines).map(r => describe(r.key, r.reason)).join('\n');
            const more = (refused.length > maxLines) ? `\n  ... and ${refused.length - maxLines} more` : '';
            text += `\n\nThese ${refused.length} entr${refused.length === 1 ? 'y' : 'ies'} will NOT be imported:\n${shown}${more}`;
        }
        if (droppedItemCount > 0) {
            const shown = dropped.slice(0, maxLines).map(d => describe(d.key, d.what)).join('\n');
            const more = (dropped.length > maxLines) ? `\n  ... and more (see the browser console)` : '';
            text += `\n\n${droppedItemCount} invalid item${droppedItemCount === 1 ? '' : 's'} or value${droppedItemCount === 1 ? '' : 's'} inside the imported settings will be left out:\n${shown}${more}`;
        }
        return text;
    }

    /**
     * Reads file content as text.
     * @param {File} file - The file to read
     * @returns {Promise<string>} Promise resolving to file content as text
     * @private
     */
    readFile(file) {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => resolve(e.target.result);
            reader.onerror = () => reject(new Error('Failed to read file'));
            reader.readAsText(file);
        });
    }

    /**
     * Restores settings from StateManager to the UI controls.
     */
    restoreSettings() {
        const advancedSearchEnabled = this.getStateManager().getAdvancedSearchEnabled();
        const checkbox = this.container.querySelector('#' + ID_ENABLE_ADVANCED_SEARCH);
        if (checkbox) {
            checkbox.checked = advancedSearchEnabled;
        }

        // Restore theme setting
        const theme = this.getStateManager().getTheme();
        const themeSelect = this.container.querySelector('#' + ID_THEME_SELECT);
        if (themeSelect) {
            themeSelect.value = theme;
        }

        // Restore inactive layer opacity (stored as decimal 0.25-1.0, displayed as 25-100)
        const inactiveLayerOpacity = this.getStateManager().getInactiveLayerOpacity();
        const opacitySlider = this.container.querySelector('#' + ID_INACTIVE_LAYER_OPACITY);
        if (opacitySlider) {
            opacitySlider.value = Math.round(inactiveLayerOpacity * 100);
            this.updateOpacityDisplay();
        }

        // Restore outside region opacity (stored as decimal 0.25-1.0, displayed as 25-100)
        const outsideRegionOpacity = this.getStateManager().getOutsideRegionOpacity();
        const outsideRegionSlider = this.container.querySelector('#' + ID_OUTSIDE_REGION_OPACITY);
        if (outsideRegionSlider) {
            outsideRegionSlider.value = Math.round(outsideRegionOpacity * 100);
            this.updateOutsideRegionOpacityDisplay();
        }

        // Restore show extra pcat info setting
        const showExtraPcatInfo = this.getStateManager().getShowExtraPcatInfo();
        const pcatCheckbox = this.container.querySelector('#' + ID_SHOW_EXTRA_PCAT_INFO);
        if (pcatCheckbox) {
            pcatCheckbox.checked = showExtraPcatInfo;
        }

        // Restore hide log panel setting
        const hideLogPanel = this.getStateManager().getHideLogPanel();
        const hideLogCheckbox = this.container.querySelector('#' + ID_HIDE_LOG_PANEL);
        if (hideLogCheckbox) {
            hideLogCheckbox.checked = hideLogPanel;
        }

        // Restore show extra action buttons setting
        const showExtraActionButtons = this.getStateManager().getShowExtraActionButtons();
        const extraActionCheckbox = this.container.querySelector('#' + ID_SHOW_EXTRA_ACTION_BUTTONS);
        if (extraActionCheckbox) {
            extraActionCheckbox.checked = showExtraActionButtons;
        }

        // Restore stay-on-map-after-read setting
        const stayOnMap = this.getStateManager().getStayOnMapAfterReadButtonPress();
        const stayOnMapCheckbox = this.container.querySelector('#' + ID_STAY_ON_MAP_AFTER_READ);
        if (stayOnMapCheckbox) {
            stayOnMapCheckbox.checked = stayOnMap;
        }

        // Restore pdf auto-highlight-on-copy setting
        const autoHighlight = this.getStateManager().getPdfAutoHighlightOnCopy();
        const autoHighlightCheckbox = this.container.querySelector('#' + ID_PDF_AUTO_HIGHLIGHT_ON_COPY);
        if (autoHighlightCheckbox) {
            autoHighlightCheckbox.checked = autoHighlight;
        }

        // Restore pdf popup-media-files setting
        const popupMediaFiles = this.getStateManager().getPdfPopupMediaFiles();
        const popupMediaFilesCheckbox = this.container.querySelector('#' + ID_PDF_POPUP_MEDIA_FILES);
        if (popupMediaFilesCheckbox) {
            popupMediaFilesCheckbox.checked = popupMediaFiles;
        }

        // Restore right margin gutter setting
        const rightMarginGutter = this.getStateManager().getRightMarginGutter();
        const rightMarginGutterSlider = this.container.querySelector('#' + ID_RIGHT_MARGIN_GUTTER);
        if (rightMarginGutterSlider) {
            rightMarginGutterSlider.value = rightMarginGutter;
            this.updateRightMarginGutterDisplay();
        }

        // Restore fit size zoom setting
        const fitSizeZoom = this.getStateManager().getFitSizeZoom();
        const fitSizeZoomSlider = this.container.querySelector('#' + ID_FIT_SIZE_ZOOM);
        if (fitSizeZoomSlider) {
            fitSizeZoomSlider.value = fitSizeZoom;
            this.updateFitSizeZoomDisplay();
        }

        // Restore play log font size setting
        const playLogFontSize = this.getStateManager().getPlayLogFontSize();
        const playLogFontSizeSlider = this.container.querySelector('#' + ID_PLAY_LOG_FONT_SIZE);
        if (playLogFontSizeSlider) {
            playLogFontSizeSlider.value = playLogFontSize;
            this.updatePlayLogFontSizeDisplay();
        }

        // Restore yellow marker style selection
        const yellowMarkerStyleId = this.getStateManager().getYellowMarkerStyleId();
        const markerPicker = this.container.querySelector('#' + ID_YELLOW_MARKER_STYLE);
        if (markerPicker) {
            markerPicker.querySelectorAll('.marker-style-item').forEach(el => {
                el.classList.toggle('selected', el.dataset.value === yellowMarkerStyleId);
            });
        }
    }

    /**
     * Saves current settings to StateManager and closes the modal.
     */
    saveSettings() {
        const checkbox = this.container.querySelector('#' + ID_ENABLE_ADVANCED_SEARCH);
        if (checkbox) {
            this.getStateManager().setAdvancedSearchEnabled(checkbox.checked);
        }

        // Save theme setting
        const themeSelect = this.container.querySelector('#' + ID_THEME_SELECT);
        if (themeSelect) {
            this.getStateManager().setTheme(themeSelect.value);
        }

        // Save inactive layer opacity (convert from 25-100 to decimal 0.25-1.0)
        const opacitySlider = this.container.querySelector('#' + ID_INACTIVE_LAYER_OPACITY);
        if (opacitySlider) {
            this.getStateManager().setInactiveLayerOpacity(parseInt(opacitySlider.value, 10) / 100);
        }

        // Save outside region opacity (convert from 25-100 to decimal 0.25-1.0)
        const outsideRegionSlider = this.container.querySelector('#' + ID_OUTSIDE_REGION_OPACITY);
        if (outsideRegionSlider) {
            this.getStateManager().setOutsideRegionOpacity(parseInt(outsideRegionSlider.value, 10) / 100);
        }

        // Save show extra pcat info setting
        const pcatCheckbox = this.container.querySelector('#' + ID_SHOW_EXTRA_PCAT_INFO);
        if (pcatCheckbox) {
            this.getStateManager().setShowExtraPcatInfo(pcatCheckbox.checked);
        }

        // Save hide log panel setting
        const hideLogCheckbox = this.container.querySelector('#' + ID_HIDE_LOG_PANEL);
        if (hideLogCheckbox) {
            this.getStateManager().setHideLogPanel(hideLogCheckbox.checked);
        }

        // Save show extra action buttons setting
        const extraActionCheckbox = this.container.querySelector('#' + ID_SHOW_EXTRA_ACTION_BUTTONS);
        if (extraActionCheckbox) {
            this.getStateManager().setShowExtraActionButtons(extraActionCheckbox.checked);
        }

        // Save stay-on-map-after-read setting
        const stayOnMapCheckbox = this.container.querySelector('#' + ID_STAY_ON_MAP_AFTER_READ);
        if (stayOnMapCheckbox) {
            this.getStateManager().setStayOnMapAfterReadButtonPress(stayOnMapCheckbox.checked);
        }

        // Save pdf auto-highlight-on-copy setting
        const autoHighlightCheckbox = this.container.querySelector('#' + ID_PDF_AUTO_HIGHLIGHT_ON_COPY);
        if (autoHighlightCheckbox) {
            this.getStateManager().setPdfAutoHighlightOnCopy(autoHighlightCheckbox.checked);
        }

        // Save pdf popup-media-files setting
        const popupMediaFilesCheckbox = this.container.querySelector('#' + ID_PDF_POPUP_MEDIA_FILES);
        if (popupMediaFilesCheckbox) {
            this.getStateManager().setPdfPopupMediaFiles(popupMediaFilesCheckbox.checked);
        }

        // Save right margin gutter setting
        const rightMarginGutterSlider = this.container.querySelector('#' + ID_RIGHT_MARGIN_GUTTER);
        if (rightMarginGutterSlider) {
            this.getStateManager().setRightMarginGutter(parseInt(rightMarginGutterSlider.value, 10));
        }

        // Save fit size zoom setting
        const fitSizeZoomSlider = this.container.querySelector('#' + ID_FIT_SIZE_ZOOM);
        if (fitSizeZoomSlider) {
            this.getStateManager().setFitSizeZoom(parseInt(fitSizeZoomSlider.value, 10));
        }

        // Save play log font size setting
        const playLogFontSizeSlider = this.container.querySelector('#' + ID_PLAY_LOG_FONT_SIZE);
        if (playLogFontSizeSlider) {
            this.getStateManager().setPlayLogFontSize(parseInt(playLogFontSizeSlider.value, 10));
        }

        // Save yellow marker style selection
        const selectedMarker = this.container.querySelector('#' + ID_YELLOW_MARKER_STYLE + ' .marker-style-item.selected');
        if (selectedMarker) {
            this.getStateManager().setYellowMarkerStyleId(selectedMarker.dataset.value);
        }

        // Apply settings immediately
        this.applySettings();

        // Close the modal (blur is handled by setupModalBlurHandler)
        this.hideModalById(SettingsViewer.MODAL_ID);
    }

    /**
     * Applies saved settings to the application UI.
     */
    applySettings() {
        const advancedSearchEnabled = this.getStateManager().getAdvancedSearchEnabled();
        this.toggleAdvancedSearchTab(advancedSearchEnabled);

        // Apply log panel visibility
        const hideLogPanel = this.getStateManager().getHideLogPanel();
        UiManager.adjustVisibilityOfLogPlayPanel(!hideLogPanel);
        if (hideLogPanel) {
            // clear contents and save changed
            this.mapApp.getPlayLogger().clearPlayLog();
        }

        // Apply play log font size
        UiManager.adjustPlayLogFontSize(this.getStateManager().getPlayLogFontSize());

        // Apply theme
        this.mapApp.applyTheme();

        // Notify MapApp to update visual options
        this.mapApp.onSettingsSaved();
    }

    /**
     * Toggle Advanced Search content between enabled and disabled states
     * @param {boolean} enabled - Whether Advanced Search is enabled
     */
    toggleAdvancedSearchTab(enabled) {
        UiManager.adjustAdvancedSearchTabVisibility(enabled);
    }

    /**
     * Updates the opacity display label to match the slider value.
     * @private
     */
    updateOpacityDisplay() {
        const opacitySlider = this.container.querySelector('#' + ID_INACTIVE_LAYER_OPACITY);
        const opacityValue = this.container.querySelector('#' + ID_INACTIVE_LAYER_OPACITY + 'Value');
        if (opacitySlider && opacityValue) {
            opacityValue.textContent = `${opacitySlider.value}%`;
        }
    }

    /**
     * Updates the outside region opacity display label to match the slider value.
     * @private
     */
    updateOutsideRegionOpacityDisplay() {
        const slider = this.container.querySelector('#' + ID_OUTSIDE_REGION_OPACITY);
        const valueEl = this.container.querySelector('#' + ID_OUTSIDE_REGION_OPACITY + 'Value');
        if (slider && valueEl) {
            valueEl.textContent = `${slider.value}%`;
        }
    }

    /**
     * Updates the right margin gutter display label to match the slider value.
     * @private
     */
    updateRightMarginGutterDisplay() {
        const slider = this.container.querySelector('#' + ID_RIGHT_MARGIN_GUTTER);
        const valueEl = this.container.querySelector('#' + ID_RIGHT_MARGIN_GUTTER + 'Value');
        if (slider && valueEl) {
            valueEl.textContent = slider.value;
        }
    }

    /**
     * Updates the fit size zoom display label to match the slider value.
     * @private
     */
    updateFitSizeZoomDisplay() {
        const slider = this.container.querySelector('#' + ID_FIT_SIZE_ZOOM);
        const valueEl = this.container.querySelector('#' + ID_FIT_SIZE_ZOOM + 'Value');
        if (slider && valueEl) {
            valueEl.textContent = slider.value;
        }
    }

    /**
     * Updates the play log font size display label to match the slider value.
     * @private
     */
    updatePlayLogFontSizeDisplay() {
        const slider = this.container.querySelector('#' + ID_PLAY_LOG_FONT_SIZE);
        const valueEl = this.container.querySelector('#' + ID_PLAY_LOG_FONT_SIZE + 'Value');
        if (slider && valueEl) {
            valueEl.textContent = slider.value;
        }
    }


    /**
     * Updates the session log display with current log messages.
     * @private
     */
    updateSessionLogDisplay() {
        const el = this.container.querySelector('#sessionLogContent');
        if (el) {
            el.innerHTML = this.mapApp.getLogHelper().getLogMessagesAsHtml();
        }
    }

    /**
     * Updates the storage space display with the current calculated size.
     * @private
     */
    updateStorageSpaceDisplay() {
        const el = this.container.querySelector('#storageSpaceUsed');
        if (el) {
            el.textContent = this.getStateManager().calcApplicationStorageSpaceUsedAsNiceSizeString();
        }
    }

    /**
     * Renders the marker style picker items that the ImageHelper manages
     * @returns {string} HTML string of marker style items
     * @private
     */
    renderMarkerStyleItems() {
        return this.mapApp.getImageHelper().renderImageListsAsMarkerStyleButtonsHtml(['markers']);
    }

    /**
     * Rebuilds the marker style picker if it is empty. The viewer is app-scoped (built once per page),
     * but the marker image list is loaded with the first world, so a viewer built before that (say,
     * opened while the first world failed to load) would otherwise keep an empty picker for good.
     * The picker's click delegation lives on the picker element itself, so it survives the rebuild.
     */
    refreshMarkerStylePickerIfEmpty() {
        const markerPicker = this.container ? this.container.querySelector('#' + ID_YELLOW_MARKER_STYLE) : null;
        if (!markerPicker || markerPicker.querySelector('.marker-style-item')) {
            return;
        }
        const itemsHtml = this.renderMarkerStyleItems();
        if (!itemsHtml) {
            return;
        }
        markerPicker.innerHTML = itemsHtml;
        this.colorizeMarkerSvgs();
    }


    /**
     * Fetches SVG marker images and replaces white fills with the configured color.
     * Non-SVG images are left unchanged.
     * @private
     */
    async colorizeMarkerSvgs() {
        const imgs = this.container.querySelectorAll('.marker-style-img');
        for (const img of imgs) {
            const src = img.getAttribute('src');
            if (!src.toLowerCase().endsWith('.svg')) continue;
            const color = img.dataset.color;
            if (!color) continue;
            try {
                const response = await fetch(src);
                const svgText = await response.text();
                const colorized = svgText
                    .replace(/fill\s*=\s*["'](?:#fff(?:fff)?|white)["']/gi, `fill="${color}"`)
                    .replace(/fill\s*:\s*(?:#fff(?:fff)?|white)/gi, `fill:${color}`);
                const blob = new Blob([colorized], { type: 'image/svg+xml' });
                img.src = URL.createObjectURL(blob);
            } catch (e) {
                // leave original image on failure
            }
        }
    }
}
